import { useState, useEffect, useCallback } from 'react'
import { listingAPI } from '../services/api/listingAPI'
import { createLogger } from '../utils/logger'

const logger = createLogger('useListingRequests')

/**
 * useListingRequests - fetches incoming listing requests for the authenticated poster.
 * @param {boolean} [enabled=true] - pass false to skip fetching (e.g. when section is collapsed)
 * Returns { requests, loading, error, refresh, respondToRequest }
 */
export function useListingRequests(enabled = true) {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    listingAPI
      .getIncomingRequests()
      .then((res) => { if (!cancelled) setRequests(res?.data?.requests || []) })
      .catch((err) => {
        if (!cancelled) {
          // 404 means the backend migration hasn't run yet — silently skip
          if (err?.response?.status === 404) {
            logger.warn('Listing requests endpoint not available yet (migration pending)')
          } else {
            logger.error('Failed to load requests', err)
          }
          setError(err)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enabled])

  useEffect(() => load(), [load])

  // Re-fetch when the user returns to the tab so the inbox is always fresh
  useEffect(() => {
    if (!enabled) return
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [enabled, load])

  const respondToRequest = useCallback(async (requestId, status, { onSuccess, onError } = {}) => {
    try {
      const res = await listingAPI.updateRequestStatus(requestId, status)
      setRequests((prev) =>
        prev.map((r) => r.id === requestId ? { ...r, status, channel_id: res.data.channel_id } : r)
      )
      onSuccess?.(res.data)
    } catch (err) {
      logger.error('Failed to respond to request', err)
      onError?.(err)
    }
  }, [])

  return { requests, loading, error, refresh: load, respondToRequest }
}
